import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Ion } from "@/components/Ion";
import { OCard, OrigHeader, PrimaryBtn, Screen, Spinner, inputCls } from "@/components/Orig";
import { useTheme } from "@/lib/theme";

const TITLES = {
  equipment_manuals: "Equipment Manuals", equipment_pricing: "Equipment Pricing", sales_programs: "Sales Programs",
  safety_resources: "Safety Resources", forms: "Forms & Submissions", marketing: "Marketing Resources", updates: "Corporate Updates",
  training_videos: "Training Videos", social_media: "Social Media Guide", brand_standards: "Brand Standards", hiring_hr: "Hiring & HR",
};

const linkMeta = (url) => {
  const u = url.toLowerCase();
  if (u.includes("youtube.com") || u.includes("youtu.be")) return ["Watch Video", "play-circle-outline"];
  if (u.includes("itunes.apple.com") || u.includes("apps.apple.com")) return ["Open in App Store", "logo-apple"];
  if (u.includes("play.google.com")) return ["Open in Play Store", "logo-google-playstore"];
  if (u.includes("jotform.com") || u.includes("form.")) return ["Open Form", "document-text-outline"];
  if (u.includes("articulate.com")) return ["Start Training", "school-outline"];
  if (u.includes("canva.")) return ["Open Guide", "book-outline"];
  if (u.includes("getjobber.com")) return ["Open Jobber", "briefcase-outline"];
  if (u.match(/\.(png|jpe?g|webp|gif)(\?|$)/)) return ["View Full Image", "image-outline"];
  if (u.includes(".pdf")) return ["Open PDF", "document-attach-outline"];
  return ["Open Link", "open-outline"];
};

const LinkBtn = ({ label, icon, onClick, testId }) => {
  const theme = useTheme();
  return (
    <button onClick={onClick} data-testid={testId} className="mt-2 flex items-center gap-1.5 text-[13px] font-bold" style={{ color: theme.accent }}>
      <Ion name={icon} size={16} color={theme.accent} />{label}
    </button>
  );
};

const ResourceCard = ({ it }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const open = (u) => window.open(u, "_blank", "noopener");
  const internal = it.link_url && (it.link_url.startsWith("/") || it.link_url.includes("laser-ops-portal"));
  const goInternal = () => { try { navigate(new URL(it.link_url).pathname); } catch { navigate(it.link_url); } };
  return (
    <OCard className="mb-2" data-testid={`resource-${it.id}`}>
      {it.image_url && <button className="block w-full" onClick={() => open(it.image_url)}><img src={it.image_url} alt={it.title} className="mb-2 h-[220px] w-full rounded-lg bg-black object-cover" /></button>}
      <div className="flex items-start justify-between gap-2">
        <p className="flex-1 text-[15px] font-bold">{it.title}</p>
        {it.price && <p className="text-sm font-extrabold" style={{ color: theme.accent }}>{it.price}</p>}
      </div>
      <p className="mt-1.5 whitespace-pre-line text-[13px] leading-5" style={{ color: theme.textSecondary }}>{it.description}</p>
      {it.pdf_url && (() => { const [l, i] = linkMeta(it.pdf_url); return <LinkBtn label={l} icon={i} onClick={() => open(it.pdf_url)} testId={`resource-pdf-${it.id}`} />; })()}
      {it.link_url && (() => {
        const [l, i] = linkMeta(it.link_url);
        return <LinkBtn label={internal ? "Open in Portal" : l} icon={internal ? "book-outline" : i} onClick={() => (internal ? goInternal() : open(it.link_url))} testId={`resource-link-${it.id}`} />;
      })()}
    </OCard>
  );
};

export function Section() {
  const { key = "" } = useParams();
  const theme = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api(`/resources?section=${encodeURIComponent(key)}`).then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  }, [key]);
  return (
    <Screen testId={`section-${key}-page`}>
      <OrigHeader eyebrow="RESOURCES" title={TITLES[key] || "Resources"} />
      {loading ? <Spinner /> : (
        <div className="px-6 pb-8">
          {items.length === 0 && <p className="mt-10 text-center" style={{ color: theme.textSecondary }} data-testid="section-empty">No items available yet.</p>}
          {items.map((it) => <ResourceCard key={it.id} it={it} />)}
        </div>
      )}
    </Screen>
  );
}

export function Support() {
  const theme = useTheme();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!subject.trim() || !message.trim()) return;
    setBusy(true);
    try {
      await api("/support", { method: "POST", body: { subject, message } });
      window.alert("Message sent. Corporate support will respond within 1 business day.");
      setSubject(""); setMessage("");
    } catch (e) { window.alert(e.message || "Failed to send"); } finally { setBusy(false); }
  };
  return (
    <Screen testId="support-page">
      <OrigHeader eyebrow="SUPPORT" title="Contact Corporate" />
      <div className="px-6 pb-8">
        <OCard className="mb-6">
          <a href="tel:+18445273799" className="flex items-center gap-2 py-2" data-testid="support-phone"><Ion name="call-outline" size={20} color={theme.accent} /><span className="text-sm font-semibold">+1 (844) 527-3799</span></a>
          <div className="h-px" style={{ background: theme.border }} />
          <a href="mailto:info@alrclean.com" className="flex items-center gap-2 py-2" data-testid="support-email"><Ion name="mail-outline" size={20} color={theme.accent} /><span className="text-sm font-semibold">info@alrclean.com</span></a>
        </OCard>
        <p className="mb-2 text-[11px] font-bold tracking-[3px]" style={{ color: theme.accent }}>SEND A MESSAGE</p>
        <input data-testid="support-subject" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={`${inputCls} mb-2`} />
        <textarea data-testid="support-message" placeholder="Describe your question or issue…" value={message} onChange={(e) => setMessage(e.target.value)} className={`${inputCls} mb-2 h-[140px] resize-none`} />
        <PrimaryBtn data-testid="support-submit" onClick={submit} disabled={busy} className="mt-4 py-4 tracking-[2px]">{busy ? "SENDING…" : "SEND MESSAGE"}</PrimaryBtn>
      </div>
    </Screen>
  );
}

export function ManualViewer() {
  const theme = useTheme();
  const [master, setMaster] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api("/manual/master").then(setMaster).catch(() => {}).finally(() => setLoading(false)); }, []);
  const src = master?.pdf_url || (master?.pdf_base64 ? (master.pdf_base64.startsWith("data:") ? master.pdf_base64 : `data:application/pdf;base64,${master.pdf_base64}`) : null);
  const ext = src?.startsWith("http");
  return (
    <Screen testId="manual-viewer-page" className="flex h-screen flex-col">
      <OrigHeader compact eyebrow="MANUAL VIEWER" title={master?.file_name || "Operations Manual"} titleSize={16}
        right={ext && <button data-testid="open-external" onClick={() => window.open(src, "_blank")} className="flex h-10 w-10 items-center justify-center rounded-[10px] border" style={{ background: theme.card, borderColor: theme.border }}><Ion name="open-outline" size={20} color={theme.accent} /></button>} />
      <div className="flex flex-1 flex-col bg-black">
        {loading ? <Spinner /> : !src ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4" data-testid="manual-empty"><Ion name="document-outline" size={48} color={theme.textSecondary} /><p className="text-sm" style={{ color: theme.textSecondary }}>No master manual uploaded yet.</p></div>
        ) : <iframe title="Operations Manual" src={src} className="w-full flex-1 border-0" data-testid="manual-iframe" />}
      </div>
    </Screen>
  );
}
